import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Flex, Form, Modal, Row, Space} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {CognitoClientsContext, SELECT_COGNITO_CLIENT, SET_COGNITO_CLIENTS_LIST,} from './context/context'
import CognitoClientsTable from './CognitoClientsTable'
import CognitoClientsFormModal from './form'
import { notifyError } from '../../../utils/notifications'

const CognitoClients = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(CognitoClientsContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef('/api/cognito_clients/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const getCognitoClientsListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'cognitoClients.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_COGNITO_CLIENTS_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'cognitoClients.loadError' }))
    }
  }

  useEffect(() => {
    getCognitoClientsListData()
  }, [])

  useEffect(() => {
    const url = `/api/cognito_clients/?search=${search}`
    currentUrl.current = url
    getCognitoClientsListData()
  }, [debouncedSearch])

  const onSetupClient = () => {
    dispatch({
      type: SELECT_COGNITO_CLIENT,
      data: { selected_client: {}, show_cognito_client_popup: true },
    })
  }

  const onEditClient = (record) => {
    dispatch({
      type: SELECT_COGNITO_CLIENT,
      data: { selected_client: record, show_cognito_client_popup: true },
    })
  }

  const deleteCognitoClient = async (record) => {
    let res = await deleteRequest(`/api/cognito_clients/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'cognitoClients.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getCognitoClientsListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'cognitoClients.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'cognitoClients.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'cognitoClients.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deleteCognitoClient(record),
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.cognito_clients_list?.length) return []
    return state.cognito_clients_list.map((item, index) => ({
      ...item,
      key: `cognito-client-${index}`,
      rowKey: `cognito-client-${item.id}`,
      index,
    }))
  }, [state.cognito_clients_list])

  return (
    <>
      {contextHolder}

      {state.show_cognito_client_popup && (
        <CognitoClientsFormModal
          getCognitoClientsListData={getCognitoClientsListData}
        />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_cognito_clients"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'cognitoClients.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="full-width"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Button onClick={onSetupClient}>
                  {intl.formatMessage({ id: 'cognitoClients.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <CognitoClientsTable
              dataSource={dataSource}
              pagination={pagination}
              getCognitoClientsListData={getCognitoClientsListData}
              onEdit={onEditClient}
              onDelete={confirmDelete}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default CognitoClients
